import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { rolesListService } from '../services/rolesList.service';
import type { Role, RoleFilters } from '../types/rolesList.types';

const initialFilters: RoleFilters = {
  query: '',
  status: 'all',
  kind: 'all',
};

export function useRolesList() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<RoleFilters>(initialFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const rolesQuery = useQuery({
    queryKey: ['settings', 'roles', 'list'],
    queryFn: rolesListService.getRoles,
  });

  const roles = useMemo(() => rolesQuery.data || [], [rolesQuery.data]);

  const filteredRoles = useMemo(() => {
    const search = filters.query.trim().toLowerCase();
    return roles.filter((role) => {
      const matchesSearch = !search || [role.name, role.code, role.description].some((value) => value.toLowerCase().includes(search));
      const matchesStatus = filters.status === 'all' || role.status === filters.status;
      const matchesKind = filters.kind === 'all' || (filters.kind === 'system' ? role.isSystem : !role.isSystem);
      return matchesSearch && matchesStatus && matchesKind;
    });
  }, [filters, roles]);

  const paginatedRoles = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredRoles.slice(startIndex, startIndex + pageSize);
  }, [currentPage, filteredRoles, pageSize]);

  const deleteRole = async (role: Role) => {
    setDeletingId(role.id);
    try {
      await rolesListService.deleteRole(role);
      await queryClient.invalidateQueries({ queryKey: ['settings', 'roles'] });
      toast.success('Đã xóa vai trò');
      return true;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Không thể xóa vai trò');
      return false;
    } finally {
      setDeletingId(null);
    }
  };

  const updateFilters = (nextFilters: RoleFilters) => {
    setFilters(nextFilters);
    setCurrentPage(1);
  };

  const updatePageSize = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    setCurrentPage(1);
  };

  return {
    roles: paginatedRoles,
    allRoles: roles,
    totalItems: filteredRoles.length,
    stats: rolesListService.getStats(roles),
    chartData: rolesListService.getChartData(roles),
    filters,
    currentPage,
    pageSize,
    deletingId,
    isLoading: rolesQuery.isLoading,
    setFilters: updateFilters,
    setCurrentPage,
    setPageSize: updatePageSize,
    deleteRole,
  };
}
